import { Alert, Button, Space, Table, Typography } from "antd";
import type { TableColumnsType } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { useAuth } from "../app/auth.tsx";
import { toAmount } from "../app/balance.ts";
import { PAGE_SIZE, todayIso, useTeacherRates, type TeacherRate } from "../app/billing.ts";
import { RuleHistoryModal, ScopeSwitch, SumText, ValidityText, VersionActions, VersionStatus, useDeleteVersion } from "./BillingRuleParts.tsx";
import { EndVersionModal } from "./EndVersionModal.tsx";
import { TeacherRateModal } from "./TeacherRateModal.tsx";

async function loadHistory(id: string): Promise<TeacherRate[]> {
  const { data, error, response } = await api.GET("/billing/teacher-rates/{id}/history", { params: { path: { id } } });
  if (!data) throw new ApiError(response.status, error);
  return data;
}

const percentFormat = new Intl.NumberFormat("uz-Latn-UZ", { maximumFractionDigits: 2 });

/** The teacher rate table by subject group, kind and grade band (spec story 57); independent of tuition. */
export function TeacherRatesTab() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("billing.manageRules");
  const [all, setAll] = useState(false);
  const [page, setPage] = useState(1);
  const rates = useTeacherRates(all ? undefined : todayIso(), page);
  const [editing, setEditing] = useState<TeacherRate | "new" | undefined>(undefined);
  const [historyOf, setHistoryOf] = useState<TeacherRate | undefined>(undefined);
  const [endOf, setEndOf] = useState<TeacherRate | undefined>(undefined);
  const remove = useDeleteVersion((id) => api.DELETE("/billing/teacher-rates/{id}", { params: { path: { id } } }));

  const groupLabel = (row: TeacherRate) => (row.subjectGroup ? t(`subjects.rateGroups.${row.subjectGroup}`) : t("billing.rates.anyGroup"));
  const bandLabel = (row: TeacherRate) =>
    row.gradeFrom === row.gradeTo ? t("billing.gradeLabel", { grade: row.gradeFrom }) : t("billing.rates.band", { from: row.gradeFrom, to: row.gradeTo });
  const keyLabel = (row: TeacherRate) => `${groupLabel(row)} · ${t(`courses.kinds.${row.kind}`)} · ${bandLabel(row)}`;

  const versionColumns: TableColumnsType<TeacherRate> = [
    { key: "rate", title: t("billing.rates.monthlyRate"), align: "right", render: (_, row) => <SumText value={row.monthlyRatePerStudent} /> },
    {
      key: "lessons",
      title: t("billing.rates.lessonsPerMonth"),
      render: (_, row) => (row.lessonsPerMonth === null ? t("billing.rates.fromTimetable") : row.lessonsPerMonth),
    },
    { key: "share", title: t("billing.rates.teacherShare"), render: (_, row) => `${percentFormat.format(toAmount(row.teacherShare) * 100)}%` },
    { key: "validity", title: t("billing.validity.title"), render: (_, row) => <ValidityText version={row} /> },
    { key: "status", title: t("billing.status.title"), render: (_, row) => <VersionStatus version={row} /> },
  ];
  const columns: TableColumnsType<TeacherRate> = [
    { key: "group", title: t("billing.rates.group"), render: (_, row) => groupLabel(row) },
    { key: "kind", title: t("billing.rates.kind"), render: (_, row) => t(`courses.kinds.${row.kind}`) },
    { key: "band", title: t("billing.rates.grades"), render: (_, row) => bandLabel(row) },
    ...versionColumns,
    {
      key: "actions",
      title: t("common.actions"),
      width: 170,
      render: (_, row) => (
        <VersionActions
          version={row}
          label={keyLabel(row)}
          canManage={canManage}
          deleting={remove.isPending && remove.variables === row.id}
          onHistory={() => setHistoryOf(row)}
          onEdit={() => setEditing(row)}
          onDelete={() => remove.mutate(row.id)}
          onEnd={() => setEndOf(row)}
        />
      ),
    },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <Typography.Text type="secondary">{t("billing.rates.hint")}</Typography.Text>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "space-between" }}>
        <ScopeSwitch all={all} onChange={(next) => { setAll(next); setPage(1); }} />
        {canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing("new")}>
            {t("billing.rates.create")}
          </Button>
        )}
      </div>
      {rates.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void rates.refetch()}>{t("app.retry")}</Button>} />}
      <Table<TeacherRate>
        rowKey="id"
        size="middle"
        loading={rates.isPending}
        columns={columns}
        dataSource={rates.data?.items ?? []}
        scroll={{ x: true }}
        locale={{ emptyText: t("billing.rates.empty") }}
        pagination={{ current: page, pageSize: PAGE_SIZE, total: Number(rates.data?.totalCount ?? 0), showSizeChanger: false, onChange: setPage, hideOnSinglePage: true }}
      />
      {canManage && <TeacherRateModal target={editing} onClose={() => setEditing(undefined)} />}
      {canManage && (
        <EndVersionModal
          version={endOf}
          label={endOf ? keyLabel(endOf) : ""}
          end={(id, validTo) => api.POST("/billing/teacher-rates/{id}/end", { params: { path: { id } }, body: { validTo } })}
          onClose={() => setEndOf(undefined)}
        />
      )}
      <RuleHistoryModal<TeacherRate>
        kind="teacher-rates"
        entityType="TeacherRate"
        version={historyOf}
        title={historyOf ? t("billing.historyTitle", { name: keyLabel(historyOf) }) : ""}
        columns={versionColumns}
        load={loadHistory}
        onClose={() => setHistoryOf(undefined)}
      />
    </Space>
  );
}
